import { useQuery } from '@tanstack/react-query';

import type { GetApproveRobosignTarPaperworkResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get approve robosign tar paperwork data for buyer session
 */
export const getApproveRobosignTarPaperwork = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetApproveRobosignTarPaperworkResponse> => {
	const response = await apiClient.get<GetApproveRobosignTarPaperworkResponse>(
		ordersEndpoints.approveRobosignTarPaperwork(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting approve robosign tar paperwork data for buyer session
 * Returns buyer info and VOS packet availability
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetApproveRobosignTarPaperwork = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.approveRobosignTarPaperwork.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get approve robosign tar paperwork data without an order ID.');
			}
			return getApproveRobosignTarPaperwork(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};

